import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Users, Music, Video, PlayCircle, Flag, Building2, Sparkles } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardValue, CardSubtitle } from '@/components/ui/Card';
import { Sparkline } from '@/components/ui/Sparkline';
import { api } from '@/lib/api';
import { COMPANY } from '@/lib/company';
import { formatNumber } from '@/lib/utils';

const linkCardClasses =
  'block transition-colors hover:border-brand hover:bg-bg-input/40 cursor-pointer';

export function Dashboard() {
  const { data } = useQuery({
    queryKey: ['metrics-legacy'],
    queryFn: async () => (await api.get('/admin/metrics')).data,
  });
  const m = data?.data;

  return (
    <Layout title="Dashboard">
      <p className="mb-6 text-sm text-text-muted">
        Visão rápida das métricas principais. Cards são clicáveis. Pra análise aprofundada, vá em <span className="text-brand">Analytics</span>.
      </p>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Link to="/users" className="contents">
          <Card className={linkCardClasses}>
            <CardHeader>
              <CardTitle>Usuários</CardTitle>
              <Users size={18} className="text-brand" />
            </CardHeader>
            <CardValue>{formatNumber(m?.users?.total)}</CardValue>
            <CardSubtitle>
              {formatNumber(m?.users?.adults)} adultos · {formatNumber(m?.users?.minors)} menores
            </CardSubtitle>
            <Sparkline data={m?.sparklines?.users} className="mt-3 text-brand" />
          </Card>
        </Link>

        <Link to="/plays" className="contents">
          <Card className={linkCardClasses}>
            <CardHeader>
              <CardTitle>Músicas</CardTitle>
              <Music size={18} className="text-brand" />
            </CardHeader>
            <CardValue>{formatNumber(m?.tracks?.published)}</CardValue>
            <CardSubtitle>+ {formatNumber(m?.tracks?.new_7d)} nos últimos 7 dias</CardSubtitle>
            <Sparkline data={m?.sparklines?.tracks} className="mt-3 text-brand" />
          </Card>
        </Link>

        <Link to="/plays" className="contents">
          <Card className={linkCardClasses}>
            <CardHeader>
              <CardTitle>Clipes</CardTitle>
              <Video size={18} className="text-brand" />
            </CardHeader>
            <CardValue>{formatNumber(m?.clips?.published)}</CardValue>
            <CardSubtitle>+ {formatNumber(m?.clips?.new_7d)} nos últimos 7 dias</CardSubtitle>
            <Sparkline data={m?.sparklines?.clips} className="mt-3 text-brand" />
          </Card>
        </Link>

        <Link to="/plays" className="contents">
          <Card className={linkCardClasses}>
            <CardHeader>
              <CardTitle>Plays (7d)</CardTitle>
              <PlayCircle size={18} className="text-brand" />
            </CardHeader>
            <CardValue>{formatNumber(m?.plays?.total_plays)}</CardValue>
            <CardSubtitle>plays de música</CardSubtitle>
            <Sparkline data={m?.sparklines?.plays} className="mt-3 text-brand" />
          </Card>
        </Link>

        <Link to="/pro" className="contents">
          <Card className={linkCardClasses}>
            <CardHeader>
              <CardTitle>Pro</CardTitle>
              <Sparkles size={18} className="text-brand" />
            </CardHeader>
            <CardValue>{formatNumber(m?.pro?.active)}</CardValue>
            <CardSubtitle>+ {formatNumber(m?.pro?.new_7d)} nos últimos 7 dias</CardSubtitle>
            <Sparkline data={m?.sparklines?.pro} className="mt-3 text-brand" />
          </Card>
        </Link>

        <Link to="/reports" className="contents">
          <Card className={linkCardClasses}>
            <CardHeader>
              <CardTitle>Denúncias</CardTitle>
              <Flag size={18} className="text-brand" />
            </CardHeader>
            <CardValue>{formatNumber(m?.reports?.pending)}</CardValue>
            <CardSubtitle>pendentes de {formatNumber(m?.reports?.total)} totais</CardSubtitle>
            <Sparkline data={m?.sparklines?.reports} className="mt-3 text-brand" />
          </Card>
        </Link>
      </div>

      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle>Dados oficiais da empresa</CardTitle>
            <Building2 size={18} className="text-brand" />
          </CardHeader>
          <div className="space-y-3 text-sm">
            {[
              ['Razão social',           COMPANY.razaoSocial],
              ['CNPJ',                   COMPANY.cnpj],
              ['Endereço da sede',       COMPANY.endereco],
              ['Endereço de correspondência', COMPANY.caixaPostal],
              ['DPO (Encarregado)',      `${COMPANY.dpo} · ${COMPANY.dpoEmail}`],
              ['Natureza · Porte',       `${COMPANY.natureza} · ${COMPANY.porte}`],
              ['CNAE principal',         COMPANY.cnaePrincipal],
              ['Data de abertura',       COMPANY.dataAbertura],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex flex-col gap-0.5 border-b border-border pb-2 last:border-0 last:pb-0"
              >
                <span className="text-xs text-text-muted">{label}</span>
                <span className="text-text-primary">{value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
